import { AttachFile, Delete, Download } from '@mui/icons-material';
import { Box, IconButton, TextField } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import classNames from 'classnames';
import { ChangeEvent, LegacyRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { openInNewTab } from '~/utils/helper';

import { useSxStyles } from './useStyles';

type Props = Omit<TextFieldProps, 'onChange' | 'value'> & {
  onChange: (value: File | undefined) => void;
  value: string | null;
  accept?: string;
  attachmentUrl?: string;
};

export const AttachmentInput = (props: Props) => {
  const {
    onChange,
    value,
    accept,
    attachmentUrl,
    disabled,
    margin,
    ...textFieldProps
  } = props;
  const { fullWidth } = textFieldProps;

  const sxStyles = useSxStyles({ margin });
  const { t } = useTranslation();

  const inputFileRef: LegacyRef<HTMLInputElement> = useRef(null);

  const handleOpenInputFilePrompt = () => {
    inputFileRef.current?.click();
  };

  const handleResetInputFile = () => {
    /* Clear hidden input file field value */
    if (!!inputFileRef.current?.value) {
      inputFileRef.current.value = '';
    }
    onChange(undefined);
  };

  const handleInputFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.files ? e.target.files[0] : undefined);
  };

  const handleDownloadAttachment = () => {
    if (!!attachmentUrl) {
      openInNewTab(attachmentUrl);
    }
  };

  return (
    <Box sx={sxStyles.root}>
      <Box
        sx={sxStyles.selectedFile}
        className={classNames(
          fullWidth && 'fullWidth',
          !!attachmentUrl && 'hasAttachmentUrl',
        )}
      >
        <TextField disabled value={value || ''} {...textFieldProps} />
        {!!attachmentUrl && (
          <Box sx={sxStyles.downloadDivider}>
            <IconButton
              onClick={() => handleDownloadAttachment()}
              title={t('laDownload')}
            >
              <Download />
            </IconButton>
          </Box>
        )}
      </Box>

      <IconButton
        onClick={() => handleOpenInputFilePrompt()}
        disabled={disabled}
      >
        <AttachFile />
      </IconButton>
      <IconButton onClick={() => handleResetInputFile()} disabled={disabled}>
        <Delete />
      </IconButton>

      <Box sx={sxStyles.fileInput}>
        <input
          ref={inputFileRef}
          accept={accept}
          type="file"
          onChange={handleInputFileChange}
        />
      </Box>
    </Box>
  );
};
